import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, ArrowLeft, KeyRound } from "lucide-react";

export default function OlvidePassword() {
  const [paso, setPaso] = useState(1);
  const [usuario, setUsuario] = useState("");
  const [codigo, setCodigo] = useState("");
  const [nueva, setNueva] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [cargando, setCargando] = useState(false);
  const navigate = useNavigate();

  const solicitar = async (e) => {
    e.preventDefault();
    setCargando(true);
    try {
      const { data } = await api.post("/auth/olvide-password", { usuario });
      setMensaje(data.mensaje);
      setPaso(2);
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setCargando(false); }
  };

  const verificar = async (e) => {
    e.preventDefault();
    setCargando(true);
    try {
      const { data } = await api.post("/auth/olvide-password/verificar", { usuario, codigo });
      setResetToken(data.reset_token);
      setPaso(3);
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setCargando(false); }
  };

  const restablecer = async (e) => {
    e.preventDefault();
    if (nueva !== confirmar) { toast.error("Las contraseñas no coinciden"); return; }
    setCargando(true);
    try {
      const { data } = await api.post("/auth/olvide-password/restablecer",
        { reset_token: resetToken, password_nueva: nueva });
      toast.success(data.mensaje);
      navigate("/login");
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setCargando(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary p-6">
      <div className="w-full max-w-sm bg-white border border-border rounded-sm p-6 shadow-sm space-y-4"
        data-testid="olvide-password-page">
        <div className="flex items-center gap-2">
          <div className="bg-primary text-primary-foreground p-2 rounded-sm">
            {paso === 3 ? <KeyRound size={20} /> : <MessageCircle size={20} />}
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold">Recuperar contraseña</h1>
            <p className="text-xs text-muted-foreground">
              {paso === 1 && "Le enviaremos un código por WhatsApp al teléfono registrado en su cuenta"}
              {paso === 2 && "Ingrese el código de 6 dígitos que recibió por WhatsApp"}
              {paso === 3 && "Defina su nueva contraseña"}
            </p>
          </div>
        </div>

        {paso === 1 && (
          <form onSubmit={solicitar} className="space-y-3" data-testid="olvide-paso-usuario">
            <div className="space-y-1.5">
              <Label htmlFor="ou">Usuario</Label>
              <Input id="ou" data-testid="olvide-usuario-input" value={usuario} autoFocus required
                autoCapitalize="none" autoCorrect="off" spellCheck={false}
                onChange={(e) => setUsuario(e.target.value)} placeholder="Su usuario o cédula" />
            </div>
            <Button data-testid="olvide-enviar-btn" type="submit" className="w-full" disabled={cargando || !usuario.trim()}>
              {cargando ? "Enviando..." : "Enviar código por WhatsApp"}
            </Button>
          </form>
        )}

        {paso === 2 && (
          <form onSubmit={verificar} className="space-y-3" data-testid="olvide-paso-codigo">
            <p className="text-xs rounded-sm bg-secondary px-3 py-2.5 text-muted-foreground" data-testid="olvide-mensaje-generico">
              {mensaje}
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="oc">Código de 6 dígitos</Label>
              <Input id="oc" data-testid="olvide-codigo-input" value={codigo} autoFocus required
                inputMode="numeric" maxLength={6} className="text-center font-mono text-lg tracking-[0.4em]"
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
            </div>
            <Button data-testid="olvide-verificar-btn" type="submit" className="w-full"
              disabled={cargando || codigo.length !== 6}>
              {cargando ? "Verificando..." : "Verificar código"}
            </Button>
            <button type="button" data-testid="olvide-reenviar-btn" onClick={() => { setPaso(1); setCodigo(""); }}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground">
              ¿No llegó? Volver a solicitar el código
            </button>
          </form>
        )}

        {paso === 3 && (
          <form onSubmit={restablecer} className="space-y-3" data-testid="olvide-paso-nueva">
            <div className="space-y-1.5">
              <Label htmlFor="on">Nueva contraseña</Label>
              <Input id="on" data-testid="olvide-nueva-input" type="password" value={nueva} autoFocus
                onChange={(e) => setNueva(e.target.value)} required minLength={6} autoComplete="new-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oc2">Confirmar nueva contraseña</Label>
              <Input id="oc2" data-testid="olvide-confirmar-input" type="password" value={confirmar}
                onChange={(e) => setConfirmar(e.target.value)} required minLength={6} autoComplete="new-password" />
            </div>
            <Button data-testid="olvide-restablecer-btn" type="submit" className="w-full" disabled={cargando}>
              {cargando ? "Guardando..." : "Restablecer contraseña"}
            </Button>
          </form>
        )}

        <Link to="/login" data-testid="olvide-volver-login"
          className="flex items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft size={13} /> Volver al inicio de sesión
        </Link>
      </div>
    </div>
  );
}
